'use client';

import config from '@haaretz/l-config';
import buildBaseImgSrc from '@haaretz/s-image-utils/buildBaseImgSrc';
import buildBaseImgSrcset from '@haaretz/s-image-utils/buildBaseImgSrcset';
import buildSizesString from '@haaretz/s-image-utils/buildSizesString';
import getAspectData from '@haaretz/s-image-utils/getAspectData';
import React from 'react';

import type { ImageAspect } from '@haaretz/s-fragments/gql-types';
import type { ImageFragment } from '@haaretz/s-fragments/HTZ_image_Image';
import type { SizesType } from '@haaretz/s-image-utils/buildSizesString';
import type { JSX } from 'react';

export type NativeSourceProps = Pick<JSX.IntrinsicElements['source'], 'srcSet' | 'media'>;

export type HtzSourceElementProps = NativeSourceProps &
  Pick<ImageFragment, 'contentId'> & {
    imgData: ImageFragment['files'][number];
    aspect: ImageAspect;
    height?: number;
    media?: string;
    width?: number;
    widths: Array<number>;
    heights?: Array<number>;
    type?: 'avif' | 'webp' | 'gif' | 'mp4';
    sizes?: SizesType;
    envOverride?: TEnv;
  };

type VideoAdditionalTypes = {
  isVideo?: boolean;
  isAnimationReduced?: boolean;
};

export default function HtzSource({
  imgData,
  aspect,
  media,
  contentId,
  widths,
  heights,
  sizes,
  type,
  envOverride,
  isVideo = false,
  isAnimationReduced = false,
}: HtzSourceElementProps & VideoAdditionalTypes) {
  const env = envOverride || config.get('env');

  const targetWidth = widths[0] || Math.min(...widths);

  const targetWidthIndex = widths.findIndex(num => num === targetWidth);

  const { isCropped, aspectRatio, aspectData } = getAspectData(aspect, imgData);
  const { height, width } = aspectData ?? { width: imgData.width, height: imgData.height };

  const matchingHeightToTargetWidth = heights?.[targetWidthIndex];

  const srcUrl = buildBaseImgSrc({
    aspect,
    aspectData,
    aspectRatio,
    contentId,
    env,
    height: matchingHeightToTargetWidth,
    imgData,
    isCropped,
    width: targetWidth,
    format: isVideo ? 'mp4' : undefined,
    frame:
      (isVideo && isAnimationReduced) || (imgData.path.includes('.gif') && isAnimationReduced)
        ? 1
        : undefined,
  });

  const srcSet = buildBaseImgSrcset({ src: srcUrl, widths, aspectRatio, heights, env });

  const getType = () => {
    if (isVideo) {
      return isAnimationReduced ? undefined : 'video/mp4';
    }
    return type ? `image/${type}` : undefined;
  };

  const overrideSrcSet = React.useMemo(() => {
    if (srcSet?.includes('.gif') && isAnimationReduced) {
      return srcSet.replaceAll(/\.(gif|mp4)(\?[^\s,]*)?/gi, (_match, ext, query) => {
        const params = new URLSearchParams(query || '');
        params.set('frame', '1');
        return `.${ext}?${params.toString()}`;
      });
    }
    return srcSet;
  }, [srcSet, isAnimationReduced]);

  const sizesString = sizes && buildSizesString(sizes);

  if (isVideo && !isAnimationReduced) {
    return <source type={getType()} data-src={srcUrl} media={media} />;
  }

  return (
    <source
      sizes={sizesString}
      width={width}
      height={height}
      type={getType()}
      media={media}
      srcSet={overrideSrcSet || srcUrl}
    />
  );
}
